import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import dayjs from 'dayjs';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import { withRouter } from 'next/router';
import * as PropTypes from 'prop-types';

import CMPInlineCallout from 'components/callouts/cmpInlineCallout';
import InDemandJobsList from 'components/jobs/list/InDemandJobsList';
import LetterLinksNav from 'components/letterLinksNav';
import {
  CAROUSEL_FILTERS,
  CMP_FILTERS,
  FILTER_PARAMS,
  FILTER_TYPES,
  setupFilters,
  WORK_FROM_HOME,
} from 'components/zpFilter/filterUtils';
import { withZippiaHistory } from 'hooks';
import SalariesByIndustries from 'layouts/salaryJobPage/salariesByIndustries';
import { SalaryByField } from 'layouts/salaryJobPage/salaryByField';
import { SalaryByLocation } from 'layouts/salaryJobPage/salaryByLocation';
import { SalaryByState } from 'layouts/salaryJobPage/salaryByState';
import { SalaryByTime } from 'layouts/salaryJobPage/salaryByTime';
import Sidebar from 'layouts/salaryJobPage/sidebar';
import QuestionAndAnswerSection from 'layouts/section/questionAndAnswerSection';
import {
  buildCmpSubpagesUrls,
  buildFullLocation,
  EVENTS,
  getLocations,
  getStateAbbr,
  isScrolledIntoView,
  isStateName,
  JOB_BUCKET_GROUP,
  mapLocationsForPaidJobs,
} from 'utils';
import JobTitleClusterFooter from 'widgets/clusterFooter/jobTitleClusterFooter/jobTitleClusterFooter';

import BottomClusterNavigation from '../components/bottomClusterNavigation';
import SalaryByExperienceLevel from '../components/SalaryByExperienceLevel';
import SalaryHistoryChartWithLocationFilter from '../components/salaryHistoyChart';
import ScrollToHash from '../components/scrollToHash';
import SubheaderListener from '../components/subheaderTabs/SubheaderListener';
import TopJobsNearYou from '../components/topJobsNearYou';
import TopProfessorsAnswer from '../components/topProfessorsAnswer';
import HeaderSection from '../layouts/salaryJobPage/HeaderSection';
import RecentAddSalaries from '../layouts/salaryJobPage/recentAddSalary';
import SalariesByCompany from '../layouts/salaryJobPage/salariesByCompany';
import SalaryMapSection from '../layouts/salaryJobPage/salaryMapSection';
import SideCardsSection from '../layouts/section/SideCardsSection';
import CommonService from '../services/commonService';
import { dispatchCreateJobBucket } from '../store/jobs/actions';
import { openSignUpModal } from '../store/modals/actions';
import { isUserOutsideUS } from '../utils';
import { logEventGTM } from '../utils/analyticsGTM';
import {
  CLUSTER_HEADER_CATEGORY,
  PAID_JOBS_CATEGORY,
  POPUP_SURVEY_SOURCE,
} from '../utils/constant';
import BreadCrumbs from '../widgets/BreadCrumbs';
import ClusterHeader from '../widgets/clusterHeader';
import Meta from '../widgets/Meta';

import styles from '../css/salaryJobPage.module.css';

const JobContainer = dynamic(() => import('../components/jobs/container/JobContainer'), {
  ssr: false,
});

const JobDismissBanner = dynamic(() => import('../widgets/dismiss/JobDismissBanner'), {
  ssr: false,
});

const propTypes = {
  data: PropTypes.object.isRequired,
  dispatchCreateJobBucket: PropTypes.func.isRequired,
};

const experienceLevelPostSubtype = 'new-experience-section';

const SECTION_IDS = {
  JOBS: 'jobs',
  SALARY_BY_STATE: 'salary-by-state',
  SALARY_BY_CITY: 'by-location',
  SALARY_BY_EXPERIENCE_LEVEL: 'salary-by-experience-level',
  SALARY_BY_COMPANY: 'by-company',
  SALARY_BY_INDUSTRY: 'by-industry',
  SALARY_BY_TRENDS: 'salary-history-line-chart',
  RECENT_JOBS: 'recent-jobs',
};

class SalaryJobPage extends React.Component {
  constructor(props) {
    super(props);

    const {
      data: { basicData },
    } = props;
    const { salaryMapExists } = basicData || {};

    this.state = {
      subheaderActiveTab: '',
      rootSectionsContainer: React.createRef(),
      showSalaryMapSection: !!salaryMapExists,
      salaryByLocationFilter: {
        ...CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
        options: {
          autoSuggest: true,
          remote: false,
        },
      },
      salaryByLocationFilterItems: [],
    };
    this.parentRef = React.createRef();
  }

  static async getInitialProps({ req, res }) {
    if (req) {
      return {
        data: res ? res.data : null,
        user: req ? req.user : null,
        locals: res ? res.locals : null,
      };
    }
    // Return page detail from __NEXT_DATA__
    return window.__NEXT_DATA__.props.pageProps;
  }

  componentDidMount() {
    const { dispatchCreateJobBucket, router } = this.props;
    if (this.parentRef) {
      this.setState({
        rootSectionsContainer: this.parentRef,
      });
    }

    const condition = router.asPath.indexOf('#by-location') !== -1;
    const node = document.getElementById('by-location');
    if (condition && node) {
      const timeoutId = setTimeout(() => {
        window.scroll({
          top: node.getBoundingClientRect().top + 100,
          left: 0,
          behavior: 'smooth',
        });
        // Clear Timeout after smooth scrolling
        clearTimeout(timeoutId);
      }, 600);
    }
    dispatchCreateJobBucket({ key: JOB_BUCKET_GROUP.RESUME_JOBS_BUCKET });
    dispatchCreateJobBucket({ key: JOB_BUCKET_GROUP.INITIAL_SALARY_JOBS_BUCKET });
    dispatchCreateJobBucket({
      key: JOB_BUCKET_GROUP.RECENT_SALARY_JOBS_BUCKET,
      detail: { hideOnEmpty: true },
    });
    dispatchCreateJobBucket({ key: JOB_BUCKET_GROUP.PAID_JOBS });
    dispatchCreateJobBucket({ key: JOB_BUCKET_GROUP.PAID_JOBS_FC_CAREER_SALARY });
  }

  sendTabTitleToSubheaderFromUserScroll = (label) => {
    this.setState({
      subheaderActiveTab: label,
    });
  };

  onSalaryMapLoadError = () => {
    this.setState({
      showSalaryMapSection: false,
    });
  };

  calloutSurvey = (event) => {
    event.preventDefault();

    const { openSignUpModal, zippiaHistory } = this.props;
    const query = {
      survey_step: 'step1',
      survey: '036',
      src: POPUP_SURVEY_SOURCE.CAREER_SALARY_PAGE,
    };
    openSignUpModal({
      ...query,
      popupType: 'header-sign-up',
    });
    zippiaHistory.pushQuery(query);
  };

  getLocations = () => {
    const { data, userLocation } = this.props;
    const { criteria } = data;
    let locations = [];
    // use userLocation
    if (userLocation && userLocation.length) {
      locations = userLocation.map((val) => ({
        city: val.city,
        state: val.city ? val.state : getStateAbbr(val.state),
        displayName: buildFullLocation(val.city, val.state, getStateAbbr(val.state)),
      }));
      // use criteria
    } else if (criteria && (criteria.geoCity || criteria.geoStateCode)) {
      locations = [
        {
          city: criteria.geoCity,
          state: criteria.geoStateCode,
          displayName: buildFullLocation(
            criteria.geoCity,
            criteria.geoState,
            criteria.geoStateCode
          ),
        },
      ];
    }
    return locations;
  };

  getJobListLocations = () => {
    const {
      data: { basicData },
    } = this.props;
    const { salaryByLocation = [] } = basicData;
    const basicDataLocations = salaryByLocation.map((salary) => ({
      city: salary.city,
      state: salary.stateCode,
    }));
    return [...this.getLocations(), ...basicDataLocations].slice(0, 10);
  };

  openSurvey() {
    const { zippiaHistory, openSignUpModal } = this.props;

    const query = {
      survey_step: 'step1',
      survey: '201',
      src: POPUP_SURVEY_SOURCE.CMP_SALARY_PAGE,
    };
    const { user } = this.props;
    const isUserLoggedIn = user && user.isLoggedIn;
    if (isUserLoggedIn) {
      query.skipQuestion = [7];
    }
    openSignUpModal({
      ...query,
    }).then(() => {
      zippiaHistory.pushQuery(query);
    });
  }

  getArticleContentBlurb = (post_subtype) => {
    const {
      data: { articleData },
    } = this.props;

    if (articleData) {
      const article = articleData.find((a) => a.post_subtype === post_subtype);
      return article && article.content ? article.content : null;
    }
  };

  updateSalaryByLocationFilter = async (_, data) => {
    try {
      const locations = data.map(({ value }) => value);
      const payload = {
        locations,
        title: this.props.data.criteria.title,
      };

      const salaryItems = locations.length
        ? await CommonService.fetchSalariesByLocation(payload)
        : [];

      this.setState(
        {
          salaryByLocationFilterItems: salaryItems,
        },
        () => {
          setTimeout(() => {
            this.scrollToSalaryByLocationSection();
          }, 0);
        }
      );
    } catch (error) {
      console.error(error);
    }
  };

  handleFilterClear = () => {
    this.setState({
      salaryByLocationFilterItems: [],
    });
  };

  scrollToSalaryByLocationSection = () => {
    const bestPayingCitiesField = document.getElementById('bestPayingCitiesField');
    // check if the window page view on best paying cities section.
    if (
      (window.pageCategory === 'Career Salary Page' ||
        window.pageCategory === 'Major Salary Page') &&
      bestPayingCitiesField &&
      isScrolledIntoView(bestPayingCitiesField)
    ) {
      // check bestPayingCities section location input value
      if (bestPayingCitiesField.value) {
        const salaryTableRows = document.querySelectorAll(
          '#by-location .new-salary-chart-container .salary-chart-item-container'
        );

        if (salaryTableRows) {
          const salaryTableRowArray = [...salaryTableRows];

          // find in #by-location table row which has city display name equal bestPayingCitiesField.value
          const scrollTargetValue = isStateName(bestPayingCitiesField.value)
            ? getStateAbbr(bestPayingCitiesField.value)
            : bestPayingCitiesField.value;
          const scrollTarget = salaryTableRowArray.find((salaryTableRow) =>
            salaryTableRow.innerHTML.includes(scrollTargetValue)
          );

          if (scrollTarget) {
            const isMobileResolution = window.innerWidth <= 768;
            // variable for avoid scroll into view of element on window edge.
            const topAdditionalScrollValue = -200;

            // unfocus input for remove keyboard on mobile devices.
            if (isMobileResolution) {
              bestPayingCitiesField.blur();
            }

            window.scroll({
              top: Math.floor(
                scrollTarget.getBoundingClientRect().top +
                  topAdditionalScrollValue +
                  window.pageYOffset
              ),
              left: 0,
              behavior: 'smooth',
            });
          }
        }
      }
    } else {
      return;
    }
  };

  logFilterButtonClickEvent = (event, source) => {
    if (event && source) {
      logEventGTM({
        event,
        filterClickSource: source,
      });
    }
  };

  getFiltersForHeaderSection = () => {
    const { basicData, hasDataForStateIndustrySalaries } = this.props.data;

    const { salaryByIndustry = [], validDegrees = [] } = basicData;

    const filters = [];

    filters.push({
      ...CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
      type: FILTER_TYPES.SINGLE,
      options: {
        autoSuggest: true,
        remote: false,
        logEvent: () =>
          this.logFilterButtonClickEvent(
            EVENTS.adjustmentFilterClick,
            'Header Location Filter Click'
          ),
      },
    });

    if (salaryByIndustry.length && hasDataForStateIndustrySalaries) {
      filters.push({
        type: FILTER_TYPES.SINGLE,
        param: 'industry',
        headerText: 'Industry',
        buttonText: 'Industry',
        options: {
          logEvent: () =>
            this.logFilterButtonClickEvent(
              EVENTS.adjustmentFilterClick,
              'Header Industry Filter Click'
            ),
        },
        valueRange: salaryByIndustry.map(({ industry }) => ({
          value: industry,
          displayName: industry,
        })),
      });
    }

    if (validDegrees.length) {
      filters.push({
        ...CAROUSEL_FILTERS[FILTER_PARAMS.EDUCATION],
        headerText: 'Education Level',
        options: {
          logEvent: () =>
            this.logFilterButtonClickEvent(
              EVENTS.adjustmentFilterClick,
              'Header Education Filter Click'
            ),
          educationSwitch: false,
        },
        valueRange: CAROUSEL_FILTERS[FILTER_PARAMS.EDUCATION].valueRange.filter(
          ({ value }) => validDegrees.indexOf(value) !== -1
        ),
      });
    }

    return filters;
  };

  handleSearchBestPayingCity = (location) => {
    const { salaryByLocationFilter } = this.state;
    const preselectedItems = [
      {
        displayName: location.displayValue,
        value: location.valueObj,
      },
      ...(salaryByLocationFilter?.preselectedItems || []),
    ];
    this.setState(
      {
        salaryByLocationFilter: {
          ...salaryByLocationFilter,
          preselectedItems,
          newButtonText:
            preselectedItems.length === 1
              ? preselectedItems[0]?.displayName
              : `${preselectedItems[0]?.displayName}...`,
        },
      },
      () => this.updateSalaryByLocationFilter('location', preselectedItems)
    );
  };

  getJobsFilter = () => {
    const {
      userLocation,
      data: { criteria },
    } = this.props;

    const filters = [
      {
        ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE],
        type: FILTER_TYPES.AUTOSUGGEST,
        onInputChange: ({ originalSearch, normalizedTitle }) => {
          const title = normalizedTitle || originalSearch || '';
          if (title) window.location.href = buildCmpSubpagesUrls(title, '', 'jobs');
        },
      },
      ...CMP_FILTERS,
    ];

    const newFields = [];

    const fields = filters.map(({ param }) => {
      switch (param) {
        case FILTER_PARAMS.JOB_TITLE:
          newFields.push({
            type: FILTER_TYPES.AUTOSUGGEST,
          });
          break;
        case FILTER_PARAMS.LOCATION:
          return {
            preselectedItems: [
              ...getLocations(userLocation, criteria),
              ...(isUserOutsideUS(criteria)
                ? []
                : [{ displayName: WORK_FROM_HOME, value: WORK_FROM_HOME }]),
            ],
          };
        default:
          return {};
      }
    });

    return setupFilters(filters, fields);
  };

  getGeoLocation = () => {
    const { criteria } = this.props.data;
    return criteria?.geoCity
      ? `${criteria?.geoCity}, ${criteria?.geoStateCode}`
      : criteria?.geoState;
  };

  getSideCardItems = () => {
    const { basicData } = this.props.data;

    const {
      highestPayingState1,
      highestPayingCity1,
      bestPayingCompany1,
      states,
      salaryByLocation,
      displayFlagOfTitleSalaryPage,
      salaryByCompany,
    } = basicData || {};

    const result = [];

    if (!_isEmpty(states) && !_isEmpty(highestPayingState1)) {
      result.push({
        title: 'Highest Paying State',
        value: highestPayingState1,
        section: 'salary-by-state',
        footerText: 'See All States',
      });
    }
    if (!_isEmpty(salaryByLocation) && !_isEmpty(highestPayingCity1)) {
      result.push({
        title: 'Highest Paying City',
        value: highestPayingCity1,
        section: 'by-location',
        footerText: 'See All Cities',
      });
    }
    if (
      displayFlagOfTitleSalaryPage &&
      !_isEmpty(salaryByCompany) &&
      !_isEmpty(bestPayingCompany1)
    ) {
      result.push({
        title: 'Highest Paying Company',
        value: bestPayingCompany1,
        section: 'by-company',
        footerText: 'See All Companies',
        isHiddenDesktop: true,
      });
    }

    return result;
  };

  getSideBarComponent = (hasSalaryReport) => {
    const { criteria, topJobsNearItems } = this.props.data;

    const siteCardItems = this.getSideCardItems();

    return (
      <>
        {!!siteCardItems?.length && (
          <SideCardsSection
            className={'mb-24'}
            items={siteCardItems}
            topJobNearItems={topJobsNearItems}
            topJobNearCalloutSrc="#src=salary-callout-jobs1010-l10"
            jobTitle={criteria.title}
          />
        )}

        <Sidebar
          hasSalaryReport={hasSalaryReport}
          jobTitle={criteria.title}
          jobTitlePlural={criteria.careerTitlePlural}
          geoLocation={this.getGeoLocation()}
          salaryCalculatorDismissible={false}
          salaryCalculatorTitleTagName={'p'}
          nonSticky={true}
          salaryCalloutsrc="salary-callout-salary2010-l20"
        />
      </>
    );
  };

  createPaidJobAPIPayload = () => {
    const { criteria } = this.props.data;
    const payload = {
      category: PAID_JOBS_CATEGORY.CAREER_CLUSTER,
      titles: [{ normalizedTitle: criteria.title, originalSearch: criteria.title }],
      locations: mapLocationsForPaidJobs(this.getLocations()),
      numJobs: 4,
    };
    return payload;
  };

  getPageSections = () => {
    const { basicData = {}, metadata, salaryHistoryChartData } = this.props.data;
    const url = metadata.url;
    const sections = [];
    // 1. Salary by State
    if (basicData.states?.length) {
      sections.push({
        url,
        title: 'State',
        id: SECTION_IDS.SALARY_BY_STATE,
        renderButton: metadata.noIndex,
      });
    }

    // 2. Salary by City
    if (basicData.salaryByLocation?.length) {
      sections.push({
        url,
        title: 'City',
        id: SECTION_IDS.SALARY_BY_CITY,
        renderButton: metadata.noIndex,
      });
    }

    // 3. Salary by Experience
    if (basicData.salaryByLevel?.length) {
      sections.push({
        url,
        title: 'Experience',
        id: SECTION_IDS.SALARY_BY_EXPERIENCE_LEVEL,
        renderButton: metadata.noIndex,
      });
    }

    // 4. Salary by Company
    if (basicData.displayFlagOfTitleSalaryPage && !_isEmpty(basicData.salaryByCompany)) {
      sections.push({
        url,
        title: 'Company',
        id: SECTION_IDS.SALARY_BY_COMPANY,
        renderButton: metadata.noIndex,
      });
    }

    // 5. Salary by Indistry
    if (basicData.displayFlagForIndustry && basicData.salaryByIndustry?.length > 3) {
      sections.push({
        url,
        title: 'Industry',
        id: SECTION_IDS.SALARY_BY_INDUSTRY,
        renderButton: metadata.noIndex,
      });
    }

    // 6. Salary by Trends
    if (!_isEmpty(salaryHistoryChartData)) {
      sections.push({
        url,
        title: 'Trends',
        id: SECTION_IDS.SALARY_BY_TRENDS,
        renderButton: metadata.noIndex,
      });
    }
    return sections;
  };

  render() {
    const {
      data,
      salaryTitleJobBucket,
      hideRecentSalaryTitleJobBucket,
      user,
      jobListDismiss,
      locals,
    } = this.props;

    const { comboSalaryLocationPopulationThreshold, comboCmpTitleResumeCountThreshold } = locals;

    const { showSalaryMapSection } = this.state;

    const hasSalaryReport = user && data.hasSalaryReport;

    const {
      recentSalaries,
      headerGraphicSalaries,
      articleData = [],
      breadCrumbs,
      relatedSalaryJobs,
      basicData,
      salaryPercentiles = [],
      criteria,
      subheaderItems,
      metadata,
      titleIndexes,
      showFooterLinks,
      salaryHistoryChartData,
      salaryStatesMapWidgetUrl,
      salaryOnlyStatesMapWidgetUrl,
      salaryStatesMapOriginalUrl,
      salaryOnlyStatesMapOriginalUrl,
      topProfessorsAnswers,
      desktopDevice,
      resumeCounts,
      footerLinksData,
      salariesForRelatedJobs,
      questionsAndAnswerData,
      jobsCountByTitleAndLocations,
      employersListCallout,
      topJobsNearItems,
      jobTitlesByLetter,
    } = data;

    const {
      displayFlagOfTitleSalaryPage,
      salaryByCompany,
      salaryByLocation,
      salaryByIndustry,
      salaryByLevel,
      displayFlagForIndustry,
      jobTitlePlural,
      states,
      salaryByTime,
      topSalariesByLocation,
    } = basicData;

    const { UPDATED_TIME } = metadata;
    const lastUpdatedDate = dayjs(UPDATED_TIME).format('MMMM D, YYYY');

    const salaryByLevelContent =
      articleData &&
      articleData.find((article) => article.post_subtype === experienceLevelPostSubtype);
    const jobsPageUrl = buildCmpSubpagesUrls(criteria.title, '', 'jobs');
    const initialLocation = (() => {
      if (user.userLocation?.length) {
        return [
          {
            city: user.userLocation[0].city,
            state: user.userLocation[0].city ? user.userLocation[0].state : '',
            stateAbbr: user.userLocation[0].city ? user.userLocation[0].state : '',
          },
        ];
      }

      if (criteria.geoStateCode) {
        return [
          {
            city: criteria.geoCity,
            state: criteria.geoState,
            stateAbbr: criteria.geoStateCode,
          },
        ];
      }

      return [];
    })();

    const answersPresent = topProfessorsAnswers && topProfessorsAnswers.length > 0;
    const firstAnswer = answersPresent && topProfessorsAnswers[0];
    const secondAnswer = answersPresent && topProfessorsAnswers[1];

    const findContentBlurb = (post_subtype) =>
      (articleData && articleData.find((article) => article.post_subtype === post_subtype)) || {};
    const salayByIndustryContentBlurb = this.getArticleContentBlurb('new-salaries-by-industry');

    const sidebarComponent = this.getSideBarComponent(hasSalaryReport);

    const locationsForPaidJobAPI =
      this.getLocations().map(({ city, state }) => ({ city, state })) || [];

    return (
      <div>
        <JobDismissBanner />
        <ClusterHeader
          data={data}
          subPageName="salary"
          subPageTextSmall={false}
          clusterCategory={CLUSTER_HEADER_CATEGORY.CAREER_SALARY}
        />

        <Meta {...metadata} />

        <ScrollToHash />

        <div
          ref={this.parentRef}
          className={classNames(styles['salary-page-container'], 'container')}
        >
          <div className="d-flex justify-content-between">
            <div className="d-block w-100 w-730">
              <section className="graphic-designer-salaries-section" id="average-salary-chart">
                <HeaderSection
                  headerGraphicSalaries={headerGraphicSalaries}
                  articleData={articleData}
                  relatedSalaryJobs={relatedSalaryJobs}
                  jobTitle={criteria.title}
                  jobTitlePlural={jobTitlePlural}
                  jobTitleArticle={criteria.jobTitleArticle}
                  showClusterSubheader
                  ARTICLE_POST_SUBTYPE="new-header-section"
                  displayFlag={displayFlagOfTitleSalaryPage}
                  lastUpdatedDate={lastUpdatedDate}
                  titleResumeCount={resumeCounts}
                  sidebarComponent={sidebarComponent}
                  filters={this.getFiltersForHeaderSection()}
                  filterGroup="salary-page-header-section"
                  showCallout={false}
                  nationalAverageHourlySalary={criteria.nationalAverageHourlySalary}
                  renderBlurb={true}
                  blurbTitle={
                    <h2 className="open-sans-large-black-bold mb-16 mt-48">{`How much does ${
                      criteria.jobTitleArticle
                    } ${criteria.title?.toLowerCase?.()} make?`}</h2>
                  }
                  sections={this.getPageSections()}
                />
              </section>
            </div>
            <div className="d-none d-xl-block mt-5">{sidebarComponent}</div>
          </div>

          <div className="section-separator-large" />

          {!salaryTitleJobBucket && (
            <Fragment>
              <section data-title="JOBS" id={SECTION_IDS.JOBS}>
                <div className="bucketBodyWithFilter">
                  <JobContainer
                    bucketContainerClass="bucketListInnerBody"
                    initialPayload={{
                      numJobs: 10,
                      title: criteria.title,
                    }}
                    filters={this.getJobsFilter()}
                    userLocation={_get(user, 'userLocation', [])}
                    criteria={criteria}
                    filterGroup={JOB_BUCKET_GROUP.RESUME_JOBS_BUCKET}
                    jobGroup={JOB_BUCKET_GROUP.RESUME_JOBS_BUCKET}
                    layout="bucket"
                    displaySeeMore={jobsPageUrl}
                    title={`${criteria.title} Jobs`}
                    isDesktop={desktopDevice}
                    jobsPerPage={14}
                    enablePaidFixedPos
                    paidJobAPIPayload={this.createPaidJobAPIPayload()}
                  />
                </div>
              </section>
              <div className="section-separator-large" />
            </Fragment>
          )}

          <div className="container position-relative p-0">
            <div className="d-flex flex-column flex-lg-row justify-content-between">
              <div className="w-730">
                {(showSalaryMapSection || !!states?.length) && (
                  <>
                    <h2 className="brandonH2 z-mb-48 w-730">{`Average ${criteria.title?.toLowerCase?.()} salary by state`}</h2>
                  </>
                )}
                {showSalaryMapSection && (
                  <section>
                    <SalaryMapSection
                      sectionTitle=""
                      articleDataContent={findContentBlurb('new-salaries-by-location')}
                      criteria={criteria}
                      onMapLoadError={this.onSalaryMapLoadError}
                      salaryStatesMapWidgetUrl={salaryStatesMapWidgetUrl}
                      salaryOnlyStatesMapWidgetUrl={salaryOnlyStatesMapWidgetUrl}
                      salaryStatesMapOriginalUrl={salaryStatesMapOriginalUrl}
                      salaryOnlyStatesMapOriginalUrl={salaryOnlyStatesMapOriginalUrl}
                    />
                  </section>
                )}
                <div className="position-relative">
                  {!!states?.length && (
                    <SalaryByState
                      tableSet={states}
                      title={criteria.title}
                      id={SECTION_IDS.SALARY_BY_STATE}
                      titlePlural={criteria.jobTitlePlural}
                      className={!showSalaryMapSection && 'mt-0'}
                      jobsCountByTitleAndLocations={jobsCountByTitleAndLocations}
                    />
                  )}
                </div>
              </div>
              <div className="d-flex flex-column">
                {(!!salaryByLocation?.length || !!states?.length) && (
                  <div className="z-mt-68 z-mt-md-0 z-mb-36">
                    <InDemandJobsList
                      inDemandJobs={data.inDemandJobs}
                      standardSideBar={true}
                      TitleTagName={'p'}
                      src="#src=salary-callout-jobs1012-l30"
                    />
                  </div>
                )}
                <div className="position-sticky top-65 z-font-base mw-350">
                  <TopJobsNearYou
                    items={topJobsNearItems}
                    jobTitle={criteria.title}
                    className={'mt-0'}
                    src={'#src=salary-callout-jobs1010-l40'}
                  />
                </div>
              </div>
            </div>

            <div className="position-relative">
              {!!salaryByLocation?.length && (
                <>
                  <div className="section-separator-large" />
                  <div id={SECTION_IDS.SALARY_BY_CITY}>
                    <SalaryByLocation
                      title={criteria.title}
                      jobTitlePlural={criteria.jobTitlePlural}
                      tableSet={salaryByLocation}
                      isResumeCountIndexable={resumeCounts >= comboCmpTitleResumeCountThreshold}
                      indexablePopulation={comboSalaryLocationPopulationThreshold}
                    />
                  </div>
                </>
              )}

              {!!topSalariesByLocation?.length && (
                <div className="z-mt-68">
                  <CMPInlineCallout
                    title={`${criteria.jobTitlePlural} are the most in-demand in these five cities:`}
                    list={topSalariesByLocation}
                    jobTitle={criteria.title}
                    btnText={`${criteria.title} jobs near me`}
                    src="#src=salary-callout-jobs1018-r50"
                    noBottomSpacing
                  />
                </div>
              )}
            </div>

            {!!(salaryByLevel?.length > 0) && (
              <>
                <div className="section-separator-large" />
                <h2 className="brandonH2 mb-5 w-730">{`${criteria.title} salary by years of experience`}</h2>
              </>
            )}

            {/* Salary By Experience Level Section Start */}
            {!!salaryByLevel?.length && (
              <section
                data-title="SALARY BY EXPERIENCE LEVEL"
                id={SECTION_IDS.SALARY_BY_EXPERIENCE_LEVEL}
              >
                <SalaryByExperienceLevel
                  title={criteria.title}
                  salaryByLevel={salaryByLevel}
                  salaryByLevelContent={salaryByLevelContent}
                />
              </section>
            )}
            {/* Salary By Experience Level Section End */}
          </div>

          {(!!salaryPercentiles?.length || !!salariesForRelatedJobs?.length) && (
            <>
              <div className="section-separator-large" />
              <SalaryByField
                jobTitle={criteria.title}
                jobTitleArticle={criteria.jobTitleArticle}
                imageExist={data?.hasSalaryImages}
                salariesForRelatedJobs={salariesForRelatedJobs}
                salaryPercentiles={salaryPercentiles}
              />
            </>
          )}

          {firstAnswer && (
            <div className="w-730 mt-68 top-professors-placeholder">
              <TopProfessorsAnswer
                answer={firstAnswer}
                jobTitle={firstAnswer.jobTitle}
                jobTitlePlural={firstAnswer.jobTitlePlural}
                QuestionTagName={'p'}
              />
            </div>
          )}
          {secondAnswer && (
            <div className="w-730 mt-68 top-professors-placeholder">
              <TopProfessorsAnswer
                answer={secondAnswer}
                jobTitle={secondAnswer.jobTitle}
                jobTitlePlural={secondAnswer.jobTitlePlural}
                QuestionTagName={'p'}
              />
            </div>
          )}
          <div className="mt-68">
            <CMPInlineCallout
              title={`Top companies hiring ${criteria.jobTitlePlural?.toLowerCase?.()} now:`}
              btnText={`Find ${
                criteria.jobTitleArticle
              } ${criteria.title?.toLowerCase?.()} job near me`}
              list={employersListCallout}
              jobTitle={criteria.title}
              src="#src=salary-callout-jobs1016-r60"
              noBottomSpacing
              noTopSpacing
            />
          </div>

          {displayFlagOfTitleSalaryPage && !_isEmpty(salaryByCompany) && (
            <Fragment>
              <div className="section-separator-large" />
              <SubheaderListener onView={this.sendTabTitleToSubheaderFromUserScroll}>
                <section data-title="BY COMPANY" id={SECTION_IDS.SALARY_BY_COMPANY}>
                  <SalariesByCompany
                    basicData={basicData}
                    articleData={articleData}
                    columnConfig={{
                      rank: {
                        columnName: 'Rank',
                      },
                      companyNameCPS: {
                        columnName: 'Company',
                        linkUrl: 'zippiaCppUrl',
                        isLink: 'toBeIndexed',
                        linkTitle: 'companyNameColLinkTitle',
                      },
                      medianDisplay: {
                        columnName: `Average Salary`,
                        customSortByField: 'median',
                        columnNameMobile: `Avg. Salary`,
                        linkUrl: 'companyTitleSalaryLink',
                        isLink: 'companyTitleSalaryLink',
                        linkTitle: 'salaryColLinkTitle',
                      },
                      hourlyRateDisplay: {
                        columnName: 'Hourly Rate',
                        customSortByField: 'hourlyRate',
                      },
                    }}
                    asTable
                  />
                </section>
              </SubheaderListener>
            </Fragment>
          )}

          {!salaryTitleJobBucket && (
            <Fragment>
              <div className="section-separator-large" />
              <section data-title="JOBS" id="jobs">
                <div className="bucketBodyWithFilter">
                  <JobContainer
                    bucketContainerClass="bucketListInnerBody"
                    initialPayload={{
                      numJobs: 10,
                      title: criteria.title,
                    }}
                    filters={this.getJobsFilter()}
                    userLocation={_get(user, 'userLocation', [])}
                    criteria={criteria}
                    filterGroup={JOB_BUCKET_GROUP.RESUME_JOBS_BUCKET}
                    jobGroup={JOB_BUCKET_GROUP.RESUME_JOBS_BUCKET}
                    layout="bucket"
                    displaySeeMore={jobsPageUrl}
                    title={`${criteria.title} Jobs`}
                    isDesktop={desktopDevice}
                    jobsPerPage={14}
                    enablePaidFixedPos
                    paidJobAPIPayload={this.createPaidJobAPIPayload()}
                  />
                </div>
              </section>
            </Fragment>
          )}

          {displayFlagForIndustry && salaryByIndustry && salaryByIndustry.length > 3 && (
            <Fragment>
              <div className="section-separator-large" />
              <SubheaderListener onView={this.sendTabTitleToSubheaderFromUserScroll}>
                <section data-title="BY INDUSTRY" id={SECTION_IDS.SALARY_BY_INDUSTRY}>
                  <SalariesByIndustries
                    basicData={basicData}
                    salayByIndustryContentBlurb={salayByIndustryContentBlurb}
                    salaryByIndustry={salaryByIndustry}
                    jobListDismiss={jobListDismiss}
                    titleIndexes={titleIndexes}
                    jobsByLocation={this.getJobListLocations()}
                    asTable
                    src="#src=salary-callout-jobs1012-l70"
                  />
                </section>
              </SubheaderListener>
            </Fragment>
          )}
          {/* This is Salary History Line Chart Section End */}

          {/* This is Salary History Line Chart Section Start */}
          <Fragment>
            <SubheaderListener>
              <section data-title="SALARY HISTORY LINE CHART" id={SECTION_IDS.SALARY_BY_TRENDS}>
                <div className="section-separator-large" />
                <h2 className="brandonH2 mb-5 w-730">{`${criteria.title} salary trends`}</h2>
                <h3 className="zp-title-h3 text-capitalize z-mb-24 w-730">
                  {`Average ${criteria.title?.toLowerCase?.()} salary over time`}
                </h3>
                <SalaryHistoryChartWithLocationFilter
                  titleKey="title"
                  titleValue={criteria.title}
                  nationalSalaries={_get(salaryHistoryChartData, 'nationalSalaries', [])}
                  salariesByStateAndCity={_get(
                    salaryHistoryChartData,
                    'salariesByStateAndCity',
                    []
                  )}
                  initialLocation={initialLocation}
                  callFunc={CommonService.fetchSalaryHistoryDataByLocation}
                  contentBlurb={this.getArticleContentBlurb('salary-trends-snippet')}
                />
              </section>
            </SubheaderListener>
          </Fragment>

          {/* This is salary over time table */}
          {!!salaryByTime?.length && (
            <SalaryByTime tableSet={salaryByTime} title={`${criteria.title} salary by year`} />
          )}

          {!hideRecentSalaryTitleJobBucket && (
            <Fragment>
              <div className="section-separator-large" />
              <section data-title="JOBS" id={SECTION_IDS.RECENT_JOBS}>
                <div className="bucketBodyWithFilter titleFilterMarginEmpty">
                  <JobContainer
                    layout="bucket"
                    useOnlyPaidJobs
                    isDesktop={desktopDevice}
                    bucketContainerClass="bucketListInnerBody"
                    paidJobAPIPayload={{
                      category: PAID_JOBS_CATEGORY.CAREER_CLUSTER,
                      titles: [{ originalSearch: criteria.title, normalizedTitle: criteria.title }],
                      locations: locationsForPaidJobAPI,
                      postingDateRange: '1d',
                      ...(!isUserOutsideUS(criteria) ? { remoteCategories: [WORK_FROM_HOME] } : {}),
                      numJobs: 20,
                    }}
                    title={`Recently added ${criteria.title?.toLowerCase?.()} jobs`}
                    paidJobGroup={JOB_BUCKET_GROUP.RECENT_SALARY_JOBS_BUCKET}
                  />
                </div>
              </section>
            </Fragment>
          )}

          {recentSalaries.salaryData && recentSalaries.salaryData.length > 0 && (
            <Fragment>
              <div className="section-separator-large" />
              <SubheaderListener onView={this.sendTabTitleToSubheaderFromUserScroll}>
                <section data-title="RECENTLY ADDED" id="recently-added">
                  <RecentAddSalaries
                    recentSalaries={recentSalaries}
                    jobTitle={criteria.title}
                    title={`Recently added ${criteria.title?.toLowerCase?.()} salaries`}
                    initialLocation={initialLocation}
                  />
                </section>
              </SubheaderListener>
            </Fragment>
          )}

          <QuestionAndAnswerSection
            title={`${criteria.title} salary FAQs`}
            data={questionsAndAnswerData}
            criteria={criteria}
            padding={false}
            findJobsTagName={'p'}
            src="#src=salary-callout-jobs1014-l80"
          />

          {showFooterLinks && (
            <Fragment>
              <div className="section-separator-large" />
              <section className="footer-links-section">
                <JobTitleClusterFooter linksData={footerLinksData} TitleTagName={'p'} />
              </section>
            </Fragment>
          )}

          {!_isEmpty(subheaderItems) && (
            <div className="container px-0">
              <BottomClusterNavigation subheaderItems={subheaderItems} />
            </div>
          )}

          <BreadCrumbs
            breadCrumbs={breadCrumbs}
            listClassName="mb-0"
            lastUpdatedDate={lastUpdatedDate}
          />

          <div className="section-separator-large p-0 z-my-20" />

          <LetterLinksNav
            title={`Browse ${criteria.industry.toLowerCase()} jobs`}
            letterLinks={jobTitlesByLetter}
            containerClassName="z-mb-20"
          />
        </div>
      </div>
    );
  }
}

SalaryJobPage.propTypes = propTypes;

const mapDispatchToProps = {
  dispatchCreateJobBucket,
  openSignUpModal,
};
const mapStateToProps = ({ jobs, user }) => {
  // false = show job container
  // true = hide job container
  const salaryTitleJobBucket = jobs[JOB_BUCKET_GROUP.RESUME_JOBS_BUCKET];
  const initialSalaryTitleJobBucket = jobs[JOB_BUCKET_GROUP.INITIAL_SALARY_JOBS_BUCKET];
  const recentSalaryTitleJobBucket = jobs[JOB_BUCKET_GROUP.RECENT_SALARY_JOBS_BUCKET];
  return {
    jobListDismiss: jobs.jobListDismiss,
    userLocation: user && user.userLocation ? user.userLocation : [],
    salaryTitleJobBucket: salaryTitleJobBucket
      ? salaryTitleJobBucket.hideOnEmpty &&
        salaryTitleJobBucket.totalJobs === 0 &&
        salaryTitleJobBucket.loading === false &&
        !salaryTitleJobBucket.error
      : false,
    initialSalaryTitleJobBucket: initialSalaryTitleJobBucket
      ? initialSalaryTitleJobBucket.hideOnEmpty &&
        initialSalaryTitleJobBucket.totalJobs === 0 &&
        initialSalaryTitleJobBucket.loading === false &&
        !initialSalaryTitleJobBucket.error
      : false,
    hideRecentSalaryTitleJobBucket: recentSalaryTitleJobBucket
      ? recentSalaryTitleJobBucket.hideOnEmpty &&
        recentSalaryTitleJobBucket.totalJobs === 0 &&
        recentSalaryTitleJobBucket.loading === false
      : false,
  };
};

export default withZippiaHistory(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(SalaryJobPage))
);
